(() => {
  if (window.diCollectionInfiniteBooted) return;
  window.diCollectionInfiniteBooted = true;

  const ROOT_SELECTOR = '#CollectionAjaxContent';
  const GRID_SELECTOR = '[data-infinite-scroll-grid]';
  const PAGINATION_SELECTOR = '[data-infinite-scroll-pagination]';
  const SENTINEL_SELECTOR = '[data-infinite-scroll-sentinel]';
  const READY_CLASS = 'di-infinite-scroll-ready';
  const RESTORING_CLASS = 'di-collection-restoring';
  const STORAGE_VERSION = 'v2';
  const STORAGE_PREFIX = `di-collection-infinite-state-${STORAGE_VERSION}::`;
  const CLICK_PREFIX = `di-collection-click-anchor-${STORAGE_VERSION}::`;
  const RESTORE_PRODUCT_PARAM = '_di_pid';
  const RESTORE_SCROLL_PARAM = '_di_sy';
  const RESTORE_VIEWPORT_RATIO_PARAM = '_di_vr';
  const STORAGE_IGNORED_PARAMS = [
    RESTORE_PRODUCT_PARAM,
    RESTORE_SCROLL_PARAM,
    RESTORE_VIEWPORT_RATIO_PARAM,
    'page',
    'section_id',
    'preview_theme_id',
    '_ab',
    '_fd',
    '_sc',
    '_pos',
    '_sid',
    '_ss',
  ];
  const LOADER_ID = 'di-collection-infinite-loader-style';
  const SCROLL_TOP_BUTTON_ID = 'di-collection-scroll-top';
  const SCROLL_DEBOUNCE_MS = 150;
  const RESTORE_MAX_AGE_MS = 1000 * 60 * 60 * 6;
  const RESTORE_CORRECTION_DELAYS = [0, 60, 160, 320, 600, 950, 1400];
  const PREFETCH_REMAINING_ITEMS = 30;
  const MOBILE_SCROLL_TOP_BASE_BOTTOM = 58;
  const MOBILE_SCROLL_TOP_FOOTER_GAP = 18;

  const state = {
    root: null,
    grid: null,
    pagination: null,
    sentinel: null,
    loader: null,
    requestId: 0,
    abortController: null,
    loading: false,
    saveTimer: null,
    key: '',
    clickKey: '',
    scrollTopButton: null,
    lastAnchor: null,
    loadedPages: new Set(),
    maxLoadedPage: 1,
    restoreApplied: false,
    restoring: false,
  };

  function supportsSessionStorage() {
    try {
      sessionStorage.setItem('__di_collection_test__', '1');
      sessionStorage.removeItem('__di_collection_test__');
      return true;
    } catch (error) {
      return false;
    }
  }

  function getNormalizedCollectionUrl(urlString) {
    let url;

    try {
      url = new URL(urlString, window.location.origin);
    } catch (error) {
      url = new URL(window.location.origin + window.location.pathname + window.location.search);
    }

    url.searchParams.delete(RESTORE_PRODUCT_PARAM);
    url.searchParams.delete(RESTORE_SCROLL_PARAM);
    url.searchParams.delete(RESTORE_VIEWPORT_RATIO_PARAM);

    return url;
  }

  function getStorageKey() {
    return STORAGE_PREFIX + getStoragePathSearch(window.location.href);
  }

  function getClickKey() {
    return CLICK_PREFIX + getStoragePathSearch(window.location.href);
  }

  function getStoragePathSearch(urlString) {
    const url = getNormalizedCollectionUrl(urlString);
    STORAGE_IGNORED_PARAMS.forEach((param) => url.searchParams.delete(param));

    const pairs = [];
    url.searchParams.forEach((value, key) => {
      pairs.push([key, value]);
    });
    pairs.sort((a, b) => {
      if (a[0] === b[0]) {
        return a[1] > b[1] ? 1 : -1;
      }
      return a[0] > b[0] ? 1 : -1;
    });

    const params = new URLSearchParams();
    pairs.forEach(([key, value]) => params.append(key, value));
    const search = params.toString();

    return `${url.pathname}${search ? `?${search}` : ''}`;
  }

  function getRestoreQueryState(urlString = window.location.href) {
    try {
      const url = new URL(urlString, window.location.origin);
      const handle = url.searchParams.get(RESTORE_PRODUCT_PARAM);
      if (!handle) return null;

      const scrollY = parseFloat(url.searchParams.get(RESTORE_SCROLL_PARAM) || '');
      const viewportRatio = parseFloat(url.searchParams.get(RESTORE_VIEWPORT_RATIO_PARAM) || '');

      return {
        handle,
        scrollY: Number.isFinite(scrollY) ? scrollY : null,
        viewportRatio: Number.isFinite(viewportRatio) ? viewportRatio : null,
      };
    } catch (error) {
      return null;
    }
  }

  function updateRestoreQueryState(anchor) {
    if (!anchor || !anchor.handle || !history.replaceState) return;

    const url = getNormalizedCollectionUrl(window.location.href);
    url.searchParams.set(RESTORE_PRODUCT_PARAM, anchor.handle);
    url.searchParams.set(RESTORE_SCROLL_PARAM, String(Math.round(window.scrollY || window.pageYOffset || 0)));

    if (typeof anchor.viewportRatio === 'number') {
      url.searchParams.set(RESTORE_VIEWPORT_RATIO_PARAM, anchor.viewportRatio.toFixed(6));
    }

    history.replaceState(history.state || {}, '', url.pathname + url.search + url.hash);
  }

  function clearRestoreQueryState() {
    if (!history.replaceState) return;

    const url = getNormalizedCollectionUrl(window.location.href);
    history.replaceState(history.state || {}, '', url.pathname + url.search + url.hash);
  }

  function parsePageNumber(urlString) {
    try {
      const url = new URL(urlString, window.location.origin);
      const value = parseInt(url.searchParams.get('page') || '1', 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    } catch (error) {
      return 1;
    }
  }

  function getCurrentPageNumber() {
    return parsePageNumber(window.location.href);
  }

  function getViewportHeight() {
    if (window.visualViewport && window.visualViewport.height) {
      return window.visualViewport.height;
    }

    return window.innerHeight || document.documentElement.clientHeight || 0;
  }

  function isMobileRestoreMode() {
    return window.matchMedia && window.matchMedia(theme && theme.config && theme.config.mediaQuerySmall ? theme.config.mediaQuerySmall : 'screen and (max-width: 769px)').matches;
  }

  function getMobileAnchorTop() {
    const viewportHeight = getViewportHeight();
    return Math.max(88, Math.min(148, viewportHeight * 0.18));
  }

  function getLoadedPagesArray() {
    return Array.from(state.loadedPages).sort((a, b) => a - b);
  }

  function setLoadedPages(pages) {
    state.loadedPages = new Set(
      (Array.isArray(pages) ? pages : [])
        .map((page) => parseInt(page, 10))
        .filter((page) => Number.isFinite(page) && page > 0)
    );

    if (!state.loadedPages.size) {
      state.loadedPages.add(getCurrentPageNumber());
    }

    state.maxLoadedPage = Math.max(...getLoadedPagesArray());
  }

  function collectPaginationLinks() {
    if (!state.pagination) return [];

    const links = Array.from(state.pagination.querySelectorAll('a[href]'));
    const seen = new Set();

    return links.map((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
          return null;
        }

        const normalized = url.pathname + '?' + url.searchParams.toString();
        if (seen.has(normalized)) {
          return null;
        }

        seen.add(normalized);
        return {
          href: url.toString(),
          page: parsePageNumber(url.toString()),
        };
      } catch (error) {
        return null;
      }
    }).filter(Boolean);
  }

  function getNavigationType() {
    try {
      const navigationEntries = performance.getEntriesByType('navigation');
      if (navigationEntries && navigationEntries.length) {
        return navigationEntries[0].type || '';
      }
    } catch (error) {}

    try {
      if (performance && performance.navigation && performance.navigation.type === 2) {
        return 'back_forward';
      }
    } catch (error) {}

    return '';
  }

  function isBackForwardNavigation() {
    return getNavigationType() === 'back_forward';
  }

  function releaseRestoreGate() {
    document.documentElement.classList.remove(RESTORING_CLASS);
  }

  function syncNodes() {
    state.root = document.querySelector(ROOT_SELECTOR);
    if (!state.root) return false;

    state.grid = state.root.querySelector(GRID_SELECTOR);
    state.pagination = state.root.querySelector(PAGINATION_SELECTOR);
    state.sentinel = state.root.querySelector(SENTINEL_SELECTOR);

    return Boolean(state.grid && state.pagination && state.sentinel);
  }

  function cancelPendingRequest() {
    if (state.abortController) {
      try {
        state.abortController.abort();
      } catch (error) {}
      state.abortController = null;
    }

    state.loading = false;
    setLoading(false);
  }

  function isRequestCurrent(request) {
    return Boolean(
      request &&
      request.id === state.requestId &&
      request.key === state.key &&
      request.key === getStorageKey() &&
      request.root === state.root &&
      request.grid === state.grid &&
      request.pagination === state.pagination &&
      request.root &&
      request.grid &&
      request.pagination &&
      request.root.isConnected &&
      request.grid.isConnected &&
      request.pagination.isConnected
    );
  }

  function getNextUrl() {
    const targetPage = state.maxLoadedPage + 1;
    const links = collectPaginationLinks();

    const exactMatch = links.find((link) => link.page === targetPage && !state.loadedPages.has(link.page));
    if (exactMatch) {
      return exactMatch.href;
    }

    const fallback = links
      .filter((link) => link.page > state.maxLoadedPage && !state.loadedPages.has(link.page))
      .sort((a, b) => a.page - b.page)[0];

    return fallback ? fallback.href : '';
  }

  function injectStyles() {
    if (document.getElementById(LOADER_ID)) return;

    const style = document.createElement('style');
    style.id = LOADER_ID;
    style.textContent = `
      ${ROOT_SELECTOR}.${READY_CLASS} [data-infinite-scroll-pagination] {
        display: none !important;
      }
      .collection-infinite-scroll-sentinel {
        display: block;
        width: 100%;
        height: 1px;
      }
      .di-collection-infinite-loader {
        display: none;
        position: fixed;
        left: 50%;
        bottom: 18px;
        z-index: 35;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border: 1px solid rgba(17, 17, 17, 0.08);
        border-radius: 999px;
        background: rgba(244, 244, 244, 0.96);
        box-shadow: 0 6px 18px rgba(17, 17, 17, 0.08);
        color: #6a6a6a;
        font-size: 10px;
        font-weight: 500;
        letter-spacing: 0.08em;
        line-height: 1;
        text-align: center;
        text-transform: uppercase;
        transform: translateX(-50%);
        pointer-events: none;
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
      }
      .di-collection-infinite-loader__label {
        white-space: nowrap;
      }
      .di-collection-infinite-loader__dots {
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }
      .di-collection-infinite-loader__dot {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: #9a9a9a;
        opacity: 0.35;
        animation: di-collection-infinite-dot 0.9s infinite ease-in-out;
      }
      .di-collection-infinite-loader__dot:nth-child(2) {
        animation-delay: 0.15s;
      }
      .di-collection-infinite-loader__dot:nth-child(3) {
        animation-delay: 0.3s;
      }
      @keyframes di-collection-infinite-dot {
        0%, 80%, 100% {
          opacity: 0.35;
          transform: scale(0.8);
        }
        40% {
          opacity: 1;
          transform: scale(1);
        }
      }
      .di-collection-infinite-loader.is-active {
        display: inline-flex;
      }
      .di-collection-scroll-top {
        display: none;
        position: fixed;
        right: 14px;
        bottom: 58px;
        z-index: 34;
        width: 56px;
        height: 56px;
        align-items: center;
        justify-content: center;
        border: 1px solid rgba(17, 17, 17, 0.12);
        border-radius: 999px;
        background: rgba(246, 246, 246, 0.98);
        box-shadow: 0 8px 22px rgba(17, 17, 17, 0.08);
        color: #111;
        line-height: 1;
        cursor: pointer;
        opacity: 0;
        transform: translateY(8px);
        transition: opacity 0.2s ease, transform 0.2s ease, background 0.2s ease;
        pointer-events: none;
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
        padding: 0;
      }
      .di-collection-scroll-top__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
      }
      .di-collection-scroll-top__icon svg {
        display: block;
        width: 24px;
        height: 24px;
      }
      .di-collection-scroll-top__icon path {
        stroke: #111;
      }
      .di-collection-scroll-top.is-visible {
        display: inline-flex;
        opacity: 1;
        transform: translateY(0);
        pointer-events: auto;
      }
      .di-collection-scroll-top:active {
        background: rgba(232, 232, 232, 0.98);
      }
      @media screen and (max-width: 769px) {
        .di-collection-infinite-loader {
          bottom: 14px;
          padding: 7px 11px;
          font-size: 9px;
        }
        .di-collection-scroll-top {
          bottom: 58px;
          right: 14px;
          width: 54px;
          height: 54px;
        }
      }
           @media screen and (min-width: 770px) {
        .di-collection-scroll-top {
           bottom: 74px!important;
    width: 45px;
    height: 45px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function ensureLoader() {
    if (!state.root || !state.sentinel) return;

    let loader = state.root.querySelector('.di-collection-infinite-loader');
    if (!loader) {
      loader = document.createElement('div');
      loader.className = 'di-collection-infinite-loader';
      loader.setAttribute('role', 'status');
      loader.setAttribute('aria-live', 'polite');
      loader.innerHTML = '<span class="di-collection-infinite-loader__label">Loading</span><span class="di-collection-infinite-loader__dots" aria-hidden="true"><span class="di-collection-infinite-loader__dot"></span><span class="di-collection-infinite-loader__dot"></span><span class="di-collection-infinite-loader__dot"></span></span>';
      state.sentinel.insertAdjacentElement('beforebegin', loader);
    }

    state.loader = loader;
  }

  function ensureScrollTopButton() {
    if (!state.root) return;

    let button = document.getElementById(SCROLL_TOP_BUTTON_ID);
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.id = SCROLL_TOP_BUTTON_ID;
      button.className = 'di-collection-scroll-top';
      button.setAttribute('aria-label', 'Scroll to top');
      button.innerHTML = '<span class="di-collection-scroll-top__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 15L12 10L7 15" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
      button.addEventListener('click', () => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      });
      document.body.appendChild(button);
    }

    state.scrollTopButton = button;
  }

  function getVisibleFixedFooterHeight() {
    if (!isMobileRestoreMode()) return 0;

    const selectors = [
      '.sticky_footer_mob',
      '.fixed-footer-bottom',
      '.sticky-add-to-cart',
      '.sticky-add-to-cart-wrapper',
      '.product-single__sticky',
      '.mobile-sticky-atc',
      '.sticky-footer',
      '.sticky-footer-nav',
      '.bottom-nav',
      '.mobile-bottom-nav',
      '.mobile-sticky-footer',
    ];

    let maxHeight = 0;
    const candidates = new Set();

    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((node) => {
        candidates.add(node);
      });
    });

    if (document.body) {
      Array.from(document.body.children).forEach((node) => {
        candidates.add(node);
      });
    }

    candidates.forEach((node) => {
      if (!(node instanceof HTMLElement) || node === state.scrollTopButton || node === state.loader) return;

      const styles = window.getComputedStyle(node);
      if (
        styles.display === 'none' ||
        styles.visibility === 'hidden' ||
        parseFloat(styles.opacity || '1') === 0
      ) {
        return;
      }

      const rect = node.getBoundingClientRect();
      if (!rect.height || rect.bottom < window.innerHeight - 4) return;

      const classText = `${node.className || ''} ${node.id || ''}`.toLowerCase();
      const isWide = rect.width >= Math.max(window.innerWidth * 0.55, window.innerWidth - 80) || (rect.left <= 20 && rect.right >= window.innerWidth - 20);
      const isFixedLike = styles.position === 'fixed' || styles.position === 'sticky';
      const isFooterLike = /footer|bottom|sticky|nav|account|wishlist|sale|home|new/.test(classText);

      if ((isFixedLike || isFooterLike) && isWide) {
        maxHeight = Math.max(maxHeight, Math.ceil(rect.height));
      }
    });

    return maxHeight;
  }

  function setLoading(active) {
    if (!state.loader) return;
    state.loader.classList.toggle('is-active', Boolean(active));
  }

  function updateScrollTopButton() {
    if (!state.scrollTopButton) return;

    const shouldShow = (window.scrollY || window.pageYOffset || 0) > 500;
    const footerHeight = shouldShow ? getVisibleFixedFooterHeight() : 0;
    state.scrollTopButton.style.bottom = `${MOBILE_SCROLL_TOP_BASE_BOTTOM + footerHeight + (footerHeight ? MOBILE_SCROLL_TOP_FOOTER_GAP : 0)}px`;
    state.scrollTopButton.classList.toggle('is-visible', shouldShow);
  }

  function findProductCardByHandle(handle) {
    if (!state.grid || !handle) return null;

    const cards = state.grid.querySelectorAll('.grid-product[data-product-handle]');
    for (let index = 0; index < cards.length; index += 1) {
      if (cards[index].getAttribute('data-product-handle') === handle) {
        return cards[index];
      }
    }

    return null;
  }

  function getAnchorFromLink(link) {
    if (!link || !link.closest) return null;

    const card = link.closest('.grid-product[data-product-handle]');
    if (!card) return null;

    const handle = card.getAttribute('data-product-handle') || '';
    if (!handle) return null;

    const viewportHeight = getViewportHeight();
    const viewportTop = card.getBoundingClientRect().top;

    return {
      handle,
      viewportTop,
      viewportHeight,
      viewportRatio: viewportHeight ? viewportTop / viewportHeight : null,
    };
  }

  function reinitializeGrid() {
    if (window.theme && typeof window.theme.reinitProductGridItem === 'function') {
      window.theme.reinitProductGridItem(state.grid);
    }

    if (typeof window.initializeFlickity === 'function') {
      window.initializeFlickity();
    }
  }

  function saveState() {
    if (!supportsSessionStorage() || !state.grid || !state.pagination || !state.key || state.restoring) return;

    const payload = {
      version: STORAGE_VERSION,
      pathSearch: getStoragePathSearch(window.location.href),
      gridHtml: state.grid.innerHTML,
      paginationHtml: state.pagination.innerHTML,
      scrollY: window.scrollY || window.pageYOffset || 0,
      loadedPages: getLoadedPagesArray(),
      anchor: state.lastAnchor ? {
        handle: state.lastAnchor.handle,
        viewportTop: state.lastAnchor.viewportTop,
        viewportHeight: state.lastAnchor.viewportHeight,
        viewportRatio: state.lastAnchor.viewportRatio,
      } : null,
      savedAt: Date.now(),
    };

    try {
      sessionStorage.setItem(state.key, JSON.stringify(payload));
    } catch (error) {}
  }

  function saveClickAnchor(anchor) {
    if (!supportsSessionStorage() || !state.clickKey || !anchor || !anchor.handle) return;

    const payload = {
      handle: anchor.handle,
      viewportTop: anchor.viewportTop,
      viewportHeight: anchor.viewportHeight,
      viewportRatio: anchor.viewportRatio,
      scrollY: window.scrollY || window.pageYOffset || 0,
      savedAt: Date.now(),
    };

    try {
      sessionStorage.setItem(state.clickKey, JSON.stringify(payload));
    } catch (error) {}
  }

  function getSavedClickAnchor() {
    if (!supportsSessionStorage() || !state.clickKey) return null;

    try {
      const payload = JSON.parse(sessionStorage.getItem(state.clickKey) || 'null');
      if (
        !payload ||
        !payload.handle ||
        typeof payload.scrollY !== 'number' ||
        !payload.savedAt ||
        Date.now() - payload.savedAt > RESTORE_MAX_AGE_MS
      ) {
        return null;
      }

      return payload;
    } catch (error) {
      return null;
    }
  }

  function clearSavedClickAnchor() {
    if (!supportsSessionStorage() || !state.clickKey) return;

    try {
      sessionStorage.removeItem(state.clickKey);
    } catch (error) {}
  }

  function resetRestoreCycle() {
    state.restoreApplied = false;
    state.restoring = false;
  }

  function scheduleSave() {
    window.clearTimeout(state.saveTimer);
    state.saveTimer = window.setTimeout(saveState, SCROLL_DEBOUNCE_MS);
  }

  function restoreState() {
    if (!supportsSessionStorage() || !state.grid || !state.pagination || !state.key || state.restoreApplied) return false;

    let payload = null;
    const pendingRestore = window.__diCollectionPendingRestore;
    if (pendingRestore && pendingRestore.key === state.key && pendingRestore.payload) {
      payload = pendingRestore.payload;
    }

    if (!payload) {
      try {
        payload = JSON.parse(sessionStorage.getItem(state.key) || 'null');
      } catch (error) {
        payload = null;
      }
    }

    if (
      !payload ||
      payload.version !== STORAGE_VERSION ||
      !payload.gridHtml ||
      typeof payload.scrollY !== 'number' ||
      !payload.savedAt ||
      Date.now() - payload.savedAt > RESTORE_MAX_AGE_MS
    ) {
      return false;
    }

    const restoreQuery = getRestoreQueryState();
    const clickAnchor = getSavedClickAnchor();
    const anchor = restoreQuery && restoreQuery.handle
      ? Object.assign({}, payload.anchor && payload.anchor.handle === restoreQuery.handle ? payload.anchor : {}, clickAnchor && clickAnchor.handle === restoreQuery.handle ? clickAnchor : {}, {
          handle: restoreQuery.handle,
          viewportRatio: typeof restoreQuery.viewportRatio === 'number'
            ? restoreQuery.viewportRatio
            : (clickAnchor && clickAnchor.handle === restoreQuery.handle
                ? clickAnchor.viewportRatio
                : payload.anchor && payload.anchor.handle === restoreQuery.handle
                  ? payload.anchor.viewportRatio
                  : null),
        })
      : (clickAnchor && clickAnchor.handle
          ? clickAnchor
          : (payload.anchor && payload.anchor.handle ? payload.anchor : null));
    const targetScroll = restoreQuery && typeof restoreQuery.scrollY === 'number'
      ? restoreQuery.scrollY
      : (clickAnchor && typeof clickAnchor.scrollY === 'number'
          ? clickAnchor.scrollY
          : payload.scrollY);

    state.restoring = true;
    state.restoreApplied = true;
    state.lastAnchor = anchor && anchor.handle ? anchor : state.lastAnchor;
    setLoadedPages(payload.loadedPages);
    state.grid.innerHTML = payload.gridHtml;
    if (typeof payload.paginationHtml === 'string') {
      state.pagination.innerHTML = payload.paginationHtml;
    }

    reinitializeGrid();
    let seededScroll = false;

    function getDesiredAnchorTop() {
      if (!anchor) return 0;
      if (isMobileRestoreMode()) {
        return getMobileAnchorTop();
      }
      if (typeof anchor.viewportRatio === 'number') {
        return anchor.viewportRatio * getViewportHeight();
      }
      return typeof anchor.viewportTop === 'number' ? anchor.viewportTop : 0;
    }

    function finishRestore() {
      state.restoring = false;
      clearSavedClickAnchor();
      clearRestoreQueryState();
      releaseRestoreGate();
      scheduleSave();
    }

    function alignScroll(attemptIndex) {
      const card = anchor ? findProductCardByHandle(anchor.handle) : null;

      if (!seededScroll) {
        if (isMobileRestoreMode() && card) {
          window.scrollTo(0, window.scrollY + card.getBoundingClientRect().top - getDesiredAnchorTop());
        } else {
          window.scrollTo(0, targetScroll);
        }
        seededScroll = true;
      }

      if (card) {
        const delta = card.getBoundingClientRect().top - getDesiredAnchorTop();
        if (Math.abs(delta) > 1) {
          window.scrollBy(0, delta);
        }
      }

      if (attemptIndex + 1 < RESTORE_CORRECTION_DELAYS.length) {
        const nextDelay = RESTORE_CORRECTION_DELAYS[attemptIndex + 1];
        window.setTimeout(() => {
          window.requestAnimationFrame(() => alignScroll(attemptIndex + 1));
        }, nextDelay);
        return;
      }

      finishRestore();
    }

    window.__diCollectionPendingRestore = null;
    window.requestAnimationFrame(() => alignScroll(0));

    return true;
  }

  function getLoadTriggerNode() {
    if (!state.grid) return null;

    const cards = Array.from(state.grid.querySelectorAll('.grid-product[data-product-handle]'));
    if (!cards.length) {
      return state.sentinel;
    }

    const triggerIndex = Math.max(0, cards.length - PREFETCH_REMAINING_ITEMS);
    return cards[triggerIndex] || state.sentinel;
  }

  function maybeTriggerLoadMore() {
    const nextUrl = getNextUrl();
    const triggerNode = getLoadTriggerNode();
    if (!triggerNode || !nextUrl) return;
    const rect = triggerNode.getBoundingClientRect();
    if (rect.top <= window.innerHeight + 300) {
      loadNextPage();
    }
  }

  function observeSentinel() {
    maybeTriggerLoadMore();
  }

  async function loadNextPage() {
    const nextUrl = getNextUrl();
    const requestedPage = parsePageNumber(nextUrl);
    if (state.loading || !nextUrl || !state.grid || !state.pagination || state.loadedPages.has(requestedPage)) return;

    const request = {
      id: state.requestId + 1,
      key: state.key,
      root: state.root,
      grid: state.grid,
      pagination: state.pagination,
    };
    const abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

    state.requestId = request.id;
    state.abortController = abortController;
    state.loading = true;
    setLoading(true);

    try {
      const response = await fetch(nextUrl, {
        credentials: 'same-origin',
        signal: abortController ? abortController.signal : undefined,
      });

      if (!response.ok) {
        throw new Error('Unable to load more products.');
      }

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const nextRoot = documentFragment.querySelector(ROOT_SELECTOR) || documentFragment;
      const nextGrid = nextRoot.querySelector(GRID_SELECTOR);
      const nextPagination = nextRoot.querySelector(PAGINATION_SELECTOR);

      if (!isRequestCurrent(request)) {
        return;
      }

      if (!nextGrid) {
        throw new Error('Missing collection grid in the next page response.');
      }

      const existingHandles = new Set(
        Array.from(state.grid.querySelectorAll('.grid-product[data-product-handle]'))
          .map((card) => card.getAttribute('data-product-handle'))
          .filter(Boolean)
      );
      const fragment = document.createDocumentFragment();

      Array.from(nextGrid.children).forEach((child) => {
        const clone = child.cloneNode(true);
        const handle = clone.getAttribute && clone.getAttribute('data-product-handle');
        if (handle && existingHandles.has(handle)) {
          return;
        }
        if (handle) {
          existingHandles.add(handle);
        }
        fragment.appendChild(clone);
      });

      state.grid.appendChild(fragment);

      if (nextPagination) {
        state.pagination.innerHTML = nextPagination.innerHTML;
      } else {
        state.pagination.innerHTML = '';
      }

      state.loadedPages.add(requestedPage);
      state.maxLoadedPage = Math.max(state.maxLoadedPage, requestedPage);
      reinitializeGrid();
      scheduleSave();
      observeSentinel();
    } catch (error) {
    } finally {
      if (state.abortController === abortController) {
        state.abortController = null;
      }

      if (request.id === state.requestId) {
        state.loading = false;
        setLoading(false);
      }
    }
  }

  function isProductLink(link) {
    if (!link || !link.href) return false;
    try {
      const url = new URL(link.href, window.location.origin);
      return url.origin === window.location.origin && url.pathname.indexOf('/products/') === 0;
    } catch (error) {
      return false;
    }
  }

  function boot(options = {}) {
    cancelPendingRequest();
    state.requestId += 1;

    if (!syncNodes()) {
      releaseRestoreGate();
      return;
    }

    state.key = getStorageKey();
    state.clickKey = getClickKey();
    if (!options.restore || !state.restoreApplied) {
      setLoadedPages([getCurrentPageNumber()]);
    }
    state.root.classList.add(READY_CLASS);
    injectStyles();
    ensureLoader();
    ensureScrollTopButton();

    if (options.restore && !state.restoreApplied) {
      const restored = restoreState();
      if (!restored) {
        clearRestoreQueryState();
        releaseRestoreGate();
      }
    } else {
      clearSavedClickAnchor();
      clearRestoreQueryState();
      releaseRestoreGate();
    }

    observeSentinel();
    updateScrollTopButton();
    if (!state.restoring) {
      scheduleSave();
    }
  }

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  document.addEventListener('click', (event) => {
    const link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!link || !state.root || !state.root.contains(link) || !isProductLink(link)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self') return;
    resetRestoreCycle();
    state.lastAnchor = getAnchorFromLink(link);
    updateRestoreQueryState(state.lastAnchor);
    saveClickAnchor(state.lastAnchor);
    saveState();
  }, true);

  document.addEventListener('input', (event) => {
    const target = event.target;
    if (target && target.closest && target.closest('.filter-form')) {
      cancelPendingRequest();
    }
  }, true);

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!target || !target.closest) return;
    if (target.closest('.filter-form') || target.closest('.tag-list--active-tags')) {
      cancelPendingRequest();
    }
  }, true);

  window.addEventListener('scroll', () => {
    if (!state.root) return;
    scheduleSave();
    maybeTriggerLoadMore();
    updateScrollTopButton();
  }, {
    passive: true,
  });

  window.addEventListener('resize', () => {
    if (!state.root) return;
    maybeTriggerLoadMore();
    updateScrollTopButton();
  });

  window.addEventListener('pagehide', () => {
    saveState();
    resetRestoreCycle();
  });

  window.addEventListener('pageshow', (event) => {
    window.setTimeout(() => {
      boot({
        restore: Boolean(getRestoreQueryState()) || event.persisted || isBackForwardNavigation(),
      });
    }, 0);
  });

  document.addEventListener('page:loaded', () => {
    window.setTimeout(() => {
      boot({
        restore: Boolean(getRestoreQueryState()) || isBackForwardNavigation(),
      });
    }, 0);
  });

  document.addEventListener('collection:reloaded', () => {
    window.setTimeout(() => {
      boot({
        restore: false,
      });
    }, 0);
  });

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    boot({
      restore: Boolean(getRestoreQueryState()) || isBackForwardNavigation(),
    });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      boot({
        restore: Boolean(getRestoreQueryState()) || isBackForwardNavigation(),
      });
    }, { once: true });
  }
})();
